/**
 * 公開 API クライアント (webroot/worker/api.ts の応答形式に対応)
 *
 * fetch 実装は差し替え可能 (setFetchImpl)。Storybook ではモック応答を差し込む (mockApi.ts)。
 */
import type { SiteInfo } from '../data/siteDefaults';
import type {
  ComposerDetail,
  ComposerSummary,
  NewsItem,
  SongDetail,
  SongSummary,
  Tag,
} from '../data/types';
import {
  composerQueryToParams,
  songQueryToParams,
  type ComposerQuery,
  type SongQuery,
} from '../lib/searchQuery';

export class ApiError extends Error {
  readonly status: number;
  readonly requestId?: string;

  constructor(status: number, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

export type FetchImpl = (input: string, init?: RequestInit) => Promise<Response>;

let fetchImpl: FetchImpl = (input, init) => fetch(input, init);

/** fetch 実装を差し替える (テスト・Storybook 用)。戻り値で元に戻せる */
export function setFetchImpl(impl: FetchImpl | null): () => void {
  const prev = fetchImpl;
  fetchImpl = impl ?? ((input, init) => fetch(input, init));
  return () => {
    fetchImpl = prev;
  };
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetchImpl(path, { signal, headers: { accept: 'application/json' } });
  if (res.ok) return (await res.json()) as T;
  let message = `HTTP ${res.status}`;
  let requestId: string | undefined;
  try {
    const body = (await res.json()) as { error?: string; requestId?: string };
    if (body.error) message = body.error;
    requestId = body.requestId;
  } catch {
    /* JSON でない本文は無視 */
  }
  throw new ApiError(res.status, message, requestId);
}

// ---- 応答型 ---------------------------------------------------------------------

export interface PageResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** 楽曲詳細 (SongDetail + 同じ作曲者の人気曲) */
export interface SongDetailResponse extends SongDetail {
  popularByComposer: SongSummary[];
}

/** 作曲者詳細 (ComposerDetail + 人気曲) */
export interface ComposerDetailResponse extends ComposerDetail {
  popularSongs: SongSummary[];
}

export interface TagsResponse {
  items: Pick<Tag, 'id' | 'category' | 'name'>[];
}

/** total / pageSize から総ページ数を求める (最低 1) */
export function totalPagesOf(page: { total: number; pageSize: number }): number {
  return Math.max(1, Math.ceil(page.total / Math.max(1, page.pageSize)));
}

// ---- エンドポイント -------------------------------------------------------------

function withQuery(path: string, params: URLSearchParams): string {
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function fetchSongs(query: SongQuery, signal?: AbortSignal) {
  return apiGet<PageResponse<SongSummary>>(
    withQuery('/api/songs', songQueryToParams(query)),
    signal,
  );
}

export function fetchSong(slug: string, signal?: AbortSignal) {
  return apiGet<SongDetailResponse>(`/api/songs/${encodeURIComponent(slug)}`, signal);
}

export function fetchComposers(query: ComposerQuery, signal?: AbortSignal) {
  return apiGet<PageResponse<ComposerSummary>>(
    withQuery('/api/composers', composerQueryToParams(query)),
    signal,
  );
}

export function fetchComposer(slug: string, signal?: AbortSignal) {
  return apiGet<ComposerDetailResponse>(`/api/composers/${encodeURIComponent(slug)}`, signal);
}

export function fetchNews(page: number, signal?: AbortSignal) {
  const params = new URLSearchParams();
  if (page > 1) params.set('page', String(page));
  return apiGet<PageResponse<NewsItem>>(withQuery('/api/news', params), signal);
}

export function fetchLatestNews(limit: number, signal?: AbortSignal) {
  return apiGet<PageResponse<NewsItem>>(`/api/news?limit=${limit}`, signal);
}

/** サイト設定のうち公開項目 (サイト名・外部リンク・コピーライト) */
export function fetchSite(signal?: AbortSignal) {
  return apiGet<SiteInfo>('/api/site', signal);
}

export function fetchTags(signal?: AbortSignal) {
  return apiGet<TagsResponse>('/api/tags', signal);
}
